/* eslint-disable no-nested-ternary */
import axios from 'axios';
import JustifyPersist from '@/domain/justify/domain/justify-persist.entity';
import Pagination from '@/domain/pagination.entity';
import Response from '@/domain/response.entity';
import ContractFilter from '@/domain/contract/entity/contract-filter.entity';
import Contract from '@/domain/contract/entity/contract.entity';
import ContractHistory from '@/domain/contract/entity/contract-version-history.entity';
import ContractSummary from '@/domain/contract/entity/contract-summary.entity';
import ContractItemRule from '@/domain/contract/entity/contract-item-rule.entity';
import ContractPersist from '@/domain/contract/entity/contract-persist.entity';
import ContractRuleRequest from '@/domain/contract/request/contract-rule-request';
import ContractReadjustment from '@/domain/contract/request/contract-readjustment.entity';
import ContractDiscount from '@/domain/contract/request/contract-discount.entity';
import CancelContractRequest from '@/domain/contract/request/cancel-contract.request';
import CancelItemRuleRequest from '@/domain/contract/request/cancel-item-rule.request';
import BoundInstanceRequest from '@/domain/contract/request/bound-instances-request.entity';
import UnboundInstance from '@/domain/contract/entity/unbound-instance.entity';
import BillableItemsFilter from '@/domain/contract/entity/billable-items-filter.entity';
import ContractItemUpdateExchangeConfigurationRequest from '@/domain/contract/request/contract-item-exchange-config-update.request';
import BindItemDatasourceRequest from '@/domain/contract/request/bind-item-datasource-request.entity';
import ContractItemAutomations from '@/domain/contract/entity/contract-item-automations.entity';
import PushOrderPersist from '@/domain/contract/entity/push-order-persist.entity';
import RequestExpirationContract from '@/domain/contract/entity/request-expiration-contract.entity';
import ContractLink from '@/domain/contract/entity/contract-link.entity';
import ContractAdditionalInfoRequest from '@/domain/contract/request/contract-additional-info-request.entity';
import DateHandler from '@/shared/utils/date-format';
import ContractFee from '../request/contract-fee.entity';
import ContractCancellationDataRequest from '../request/contract-cancellation-data-request.entity';

export type ExecuteSingleBillingParams = {
  invoiceCompetence: string;
  contractItemId: string;
  businessUnitId: string;
}

export default class ContractService {
  public static getParams(pagination: Pagination, filter: ContractFilter): Object {
    const parameters = {
      page: pagination.page - 1,
      size: pagination.itemsPerPage,
      sortBy: pagination.arrangeSortBy,
      contract: filter.contract ? filter.contract : null,
      periodStart: filter.periodStart ? DateHandler.formatToIsoDate(filter.periodStart) : null,
      periodEnd: filter.periodEnd ? DateHandler.formatToIsoDate(filter.periodEnd) : null,
      customerIds: filter.customerIds ? filter.customerIds.join(',') : null,
      customerDocument: filter.customerDocument ? filter.customerDocument : null,
      status: filter.status,
      versionExternalId: filter.externalId ? [filter.externalId] : null,
      instanceCode: filter.instanceCode ? filter.instanceCode : null,
      resultCenterId: filter.resultCenterId ? filter.resultCenterId : null,
      beneficiary: filter.beneficiary,
      competenceFilter: filter.competenceFilter,
      expiresAtStart: filter.expiresAtStart
        ? DateHandler.formatToIsoDate(filter.expiresAtStart) : null,
      expiresAtEnd: filter.expiresAtEnd
        ? DateHandler.formatToIsoDate(filter.expiresAtEnd) : null,
      anniversaryPeriodStart: filter.anniversaryPeriodStart
        ? DateHandler.formatToIsoDate(filter.anniversaryPeriodStart) : null,
      anniversaryPeriodEnd: filter.anniversaryPeriodEnd
        ? DateHandler.formatToIsoDate(filter.anniversaryPeriodEnd) : null,
      billingAnniversaryType: filter.billingAnniversaryType,
      markedToExpire: filter.markedToExpire,
      productIds: filter.productIds ? filter.productIds : null,
      frequency: filter.frequency ? filter.frequency : null,
      isCustomBilling: filter.isCustomBilling,
      ruleGroupIds: filter.ruleGroupIds ? filter.ruleGroupIds.join(',') : null,
      isReviewed: filter.isReviewed,
      filterInterval: filter.filterInterval,
      robotFilterType: filter.robotFilterType,
      contractGroupId: filter.contractGroupId,
      contractGroupLinkedType: filter.contractGroupLinkedType,
      billingDaily: filter.billingDaily,
      contractModel: filter.contractModel,
    }
    return parameters;
  }

  public static filter(pagination: Pagination, businessUnitId: string, filter: ContractFilter) {
    const parameter = { params: this.getParams(pagination, filter) };
    return axios.get(`/api/v1/business-units/${businessUnitId}/contracts`, parameter)
      .then((response: any) => Response.of(response.data));
  }

  public static filterContractItemRuleReport(
    businessUnitId: string,
    filter: ContractFilter,
  ) {
    return axios.get(`/api/v1/business-units/${businessUnitId}/contracts/item-rule-report`, {
      params: {
        contract: filter.contract ? filter.contract : null,
        periodStart: filter.periodStart ? DateHandler.formatToIsoDate(filter.periodStart) : null,
        periodEnd: filter.periodEnd ? DateHandler.formatToIsoDate(filter.periodEnd) : null,
        customerIds: filter.customerIds ? filter.customerIds.join(',') : null,
        customerDocument: filter.customerDocument ? filter.customerDocument : null,
        status: filter.status,
        versionExternalId: filter.externalId ? [filter.externalId] : null,
        instanceCode: filter.instanceCode ? filter.instanceCode : null,
        resultCenterId: filter.resultCenterId ? filter.resultCenterId : null,
        beneficiary: filter.beneficiary,
        competenceFilter: filter.competenceFilter,
        expiresAtStart: filter.expiresAtStart
          ? DateHandler.formatToIsoDate(filter.expiresAtStart) : null,
        expiresAtEnd: filter.expiresAtEnd
          ? DateHandler.formatToIsoDate(filter.expiresAtEnd) : null,
        anniversaryPeriodStart: filter.anniversaryPeriodStart
          ? DateHandler.formatToIsoDate(filter.anniversaryPeriodStart) : null,
        anniversaryPeriodEnd: filter.anniversaryPeriodEnd
          ? DateHandler.formatToIsoDate(filter.anniversaryPeriodEnd) : null,
        billingAnniversaryType: filter.billingAnniversaryType,
        markedToExpire: filter.markedToExpire,
        productIds: filter.productIds ? filter.productIds : null,
        frequency: filter.frequency ? filter.frequency : null,
        isCustomBilling: filter.isCustomBilling,
        isReviewed: filter.isReviewed,
        filterInterval: filter.filterInterval,
        robotFilterType: filter.robotFilterType,
        contractGroupId: filter.contractGroupId,
        contractGroupLinkedType: filter.contractGroupLinkedType,
      },
    })
      .then((response: any) => response.data);
  }

  public static filterContractPlanReport(
    businessUnitId: string,
    filter: ContractFilter,
  ) {
    return axios.get(`/api/v1/business-units/${businessUnitId}/contracts/contract-plan-report`, {
      params: {
        contract: filter.contract ? filter.contract : null,
        periodStart: filter.periodStart ? DateHandler.formatToIsoDate(filter.periodStart) : null,
        periodEnd: filter.periodEnd ? DateHandler.formatToIsoDate(filter.periodEnd) : null,
        customerIds: filter.customerIds ? filter.customerIds.join(',') : null,
        customerDocument: filter.customerDocument ? filter.customerDocument : null,
        status: filter.status,
        versionExternalId: filter.externalId ? [filter.externalId] : null,
        instanceCode: filter.instanceCode ? filter.instanceCode : null,
        resultCenterId: filter.resultCenterId ? filter.resultCenterId : null,
        beneficiary: filter.beneficiary,
        competenceFilter: filter.competenceFilter,
        expiresAtStart: filter.expiresAtStart
          ? DateHandler.formatToIsoDate(filter.expiresAtStart) : null,
        expiresAtEnd: filter.expiresAtEnd
          ? DateHandler.formatToIsoDate(filter.expiresAtEnd) : null,
        anniversaryPeriodStart: filter.anniversaryPeriodStart
          ? DateHandler.formatToIsoDate(filter.anniversaryPeriodStart) : null,
        anniversaryPeriodEnd: filter.anniversaryPeriodEnd
          ? DateHandler.formatToIsoDate(filter.anniversaryPeriodEnd) : null,
        billingAnniversaryType: filter.billingAnniversaryType,
        markedToExpire: filter.markedToExpire,
        isCustomBilling: filter.isCustomBilling,
        isReviewed: filter.isReviewed,
        filterInterval: filter.filterInterval,
        robotFilterType: filter.robotFilterType,
        contractGroupId: filter.contractGroupId,
        contractGroupLinkedType: filter.contractGroupLinkedType,
        billingDaily: filter.billingDaily,
      },
    })
      .then((response: any) => response.data);
  }

  public static filterContractItemBillingReport(
    businessUnitId: string,
    filter: ContractFilter,
  ) {
    return axios.get(`/api/v1/business-units/${businessUnitId}/contracts/item-billing-report`, {
      params: {
        contract: filter.contract ? filter.contract : null,
        periodStart: filter.periodStart ? DateHandler.formatToIsoDate(filter.periodStart) : null,
        periodEnd: filter.periodEnd ? DateHandler.formatToIsoDate(filter.periodEnd) : null,
        customerIds: filter.customerIds ? filter.customerIds.join(',') : null,
        customerDocument: filter.customerDocument ? filter.customerDocument : null,
        status: filter.status,
        versionExternalId: filter.externalId ? [filter.externalId] : null,
        instanceCode: filter.instanceCode ? filter.instanceCode : null,
        resultCenterId: filter.resultCenterId ? filter.resultCenterId : null,
        beneficiary: filter.beneficiary,
        competenceFilter: filter.competenceFilter,
        expiresAtStart: filter.expiresAtStart
          ? DateHandler.formatToIsoDate(filter.expiresAtStart) : null,
        expiresAtEnd: filter.expiresAtEnd
          ? DateHandler.formatToIsoDate(filter.expiresAtEnd) : null,
        anniversaryPeriodStart: filter.anniversaryPeriodStart
          ? DateHandler.formatToIsoDate(filter.anniversaryPeriodStart) : null,
        anniversaryPeriodEnd: filter.anniversaryPeriodEnd
          ? DateHandler.formatToIsoDate(filter.anniversaryPeriodEnd) : null,
        billingAnniversaryType: filter.billingAnniversaryType,
        markedToExpire: filter.markedToExpire,
        productIds: filter.productIds ? filter.productIds : null,
        frequency: filter.frequency ? filter.frequency : null,
        isCustomBilling: filter.isCustomBilling,
        isReviewed: filter.isReviewed,
        filterInterval: filter.filterInterval,
        robotFilterType: filter.robotFilterType,
        contractGroupId: filter.contractGroupId,
        contractGroupLinkedType: filter.contractGroupLinkedType,
      },
    })
      .then((response: any) => response.data);
  }

  public static getContract(businessUnitId: string, contractId: string): Promise<Contract> {
    return axios.get(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}`)
      .then((response: any) => Contract.of(response.data));
  }

  public static getCancelItemRules(contractId: string): Promise<CancelItemRuleRequest[]> {
    return axios.get(`/api/v1/contracts/${contractId}/cancel-item-rules`)
      .then((response: any) => {
        const items = response.data?.items || [];
        const rules: CancelItemRuleRequest[] = [];

        items.forEach((item: any) => {
          const businessUnitName = item.businessUnit?.name;
          const itemPrefix = businessUnitName
            ? `[unidade: ${businessUnitName}] ITEM ${item.number}`
            : `ITEM ${item.number}`;

          (item.rules || []).forEach((rule: any) => {
            rules.push(CancelItemRuleRequest.of({
              contractItemRuleId: rule.contractItemRuleId,
              description: `${itemPrefix} - ${rule.ruleName}`,
              billingStrategy: '',
              billingValue: '',
            }));
          });
        });
        return rules;
      });
  }

  public static getContractHistory(
    businessUnitId: string, contractId: string,
  ): Promise<ContractHistory> {
    return axios.get(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/versions/history`)
      .then((response: any) => ContractHistory.of(response.data));
  }

  public static getContractsSummary(
    businessId: string,
    competence: string,
  ): Promise<ContractSummary> {
    return axios
      .get(`/api/v1/business-units/${businessId}/contracts/summaryNew?competence=${competence}`)
      .then((response: any) => ContractSummary.of(response.data));
  }

  public static updateContract(
    businessUnitId: string, contractId: string, contractPersist: ContractPersist,
  ): Promise<void> {
    return axios
      .put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}`, contractPersist);
  }

  public static enableBilling(businessId: string, contractId: string)
    : Promise<void> {
    return axios
      .put(`/api/v1/business-units/${businessId}/contracts/${contractId}/enable-billing`);
  }

  public static disableBilling(
    businessId: string,
    contractId: string,
    justify: JustifyPersist,
    file: any,
  ) : Promise<void> {
    const formData = new FormData();
    formData.append('json', JSON.stringify({
      justificationId: justify.justificationId,
      observation: justify.observation,
      competenceStart: justify.competenceStart,
      competenceEnd: justify.competenceEnd,
    }));
    formData.append('file', file);
    return axios
      .put(`/api/v1/business-units/${businessId}/contracts/${contractId}/disable-billing`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        maxContentLength: Infinity,
      });
  }

  public static getContractItemRules(businessId: string, contractItemId: string)
    : Promise<ContractItemRule> {
    return axios
      .get(`/api/v1/business-units/${businessId}/contract-items/${contractItemId}/rules`)
      .then((response: any) => ContractItemRule.of(response.data));
  }

  public static persistRules(businessId: string, contractItemId: string,
    contractItemRules: ContractRuleRequest[]): Promise<void> {
    return axios
      .put(`/api/v1/business-units/${businessId}/contract-items/${contractItemId}/rules`, contractItemRules);
  }

  public static disableContractItemRule(businessId: string, contractItemId: string,
    ruleId: string, justify: JustifyPersist): Promise<void> {
    return axios
      .put(`/api/v1/business-units/${businessId}/contract-items/${contractItemId}/rules/${ruleId}/disable`, justify);
  }

  public static enableContractItemRule(businessId: string, contractItemId: string,
    ruleId: string): Promise<void> {
    return axios
      .put(`/api/v1/business-units/${businessId}/contract-items/${contractItemId}/rules/${ruleId}/enable`);
  }

  public static getMacros(businessUnitId: string, contractItemId: string): Promise<string[]> {
    return axios.get(`/api/v1/business-units/${businessUnitId}/contract-items/${contractItemId}/macro-names`)
      .then((response: any) => response.data);
  }

  public static updateObservation(
    businessUnitId: string,
    itemId: string,
    observation: string,
  ): Promise<any> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/contract-items/${itemId}/observation`, {
      observation,
    });
  }

  public static saveReadjustmentConfig(businessUnitId: string, contractId: string,
    request: ContractReadjustment): Promise<void> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/readjustment`, request);
  }

  public static saveDiscountConfig(
    businessUnitId: string,
    contractId: string,
    discountRequest: ContractDiscount,
  ): Promise<void> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/discount`, discountRequest);
  }

  public static saveFeeConfig(
    businessUnitId: string,
    contractId: string,
    feeRequest: ContractFee,
  ): Promise<void> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/fee`, feeRequest);
  }

  public static cancelContract(businessUnitId: string, contractId: string,
    request: CancelContractRequest): Promise<void> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/cancel`, request);
  }

  public static bindInstancesToItem(
    businessUnitId: string,
    itemId: string,
    boundInstances: BoundInstanceRequest,
  ): Promise<any> {
    return axios.put(`/api/v1/business-units/${businessUnitId}/contract-items/${itemId}/instances`, boundInstances);
  }

  public static bindUnboundInstances(
    businessUnitId: string,
    contractId: string,
    unboundInstances: UnboundInstance[],
  ): Promise<void> {
    return axios.post(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/instances/bind`, unboundInstances);
  }

  public static getBillableItems(businessUnitId: string, filter: BillableItemsFilter,
    page: number, size: number): Promise<Response> {
    return axios.get(`/api/v1/business-units/${businessUnitId}/contract-items/billable`, {
      params: {
        customerExternalId: filter.customerExternalId,
        customerId: filter.customerId,
        competence: filter.competence,
        page: page - 1,
        size,
      },
    }).then((resp: any) => Response.of(resp.data));
  }

  public static performSingleBilling({
    businessUnitId,
    contractItemId,
    invoiceCompetence,
  }: ExecuteSingleBillingParams): Promise<string> {
    return axios
      .get(`/api/v1/business-units/${businessUnitId}/contract-items/${contractItemId}/perform-single-billing`,
        {
          params: {
            competence: invoiceCompetence,
          },
        })
      .then((response: any) => response.data);
  }

  public static performItemsBilling(
    businessUnitId: string,
    contractItemIds: Array<string>,
    competence: string,
  ): Promise<Array<string>> {
    return axios
      .get(`/api/v1/business-units/${businessUnitId}/contract-items/perform-billing`, {
        params: {
          contractItemIds: contractItemIds.join(','),
          competence,
        },
      }).then((response: any) => response.data);
  }

  public static performRetroactiveBilling(businessUnitId: string,
    contractItemId: string, competences: string, invoiceCompetence: string,
    startDate: string, endDate: string): Promise<string> {
    return axios
      .get(`/api/v1/business-units/${businessUnitId}/contract-items/${contractItemId}/perform-retroactive-billing`, {
        params: {
          competences,
          invoiceCompetence,
          startDate,
          endDate,
        },
      }).then((response: any) => response.data);
  }

  public static updateItemExchangeConfiguration(businessUnitId: string,
    contractItemId: string, body: ContractItemUpdateExchangeConfigurationRequest): Promise<string> {
    return axios
      .put(`/api/v1/business-units/${businessUnitId}/contract-items/${contractItemId}/exchange-configuration`, body)
  }

  public static bindItemDatasource(
    businessUnitId: string,
    itemId: string,
    request: BindItemDatasourceRequest,
  ): Promise<void> {
    return axios.patch(`/api/v1/business-units/${businessUnitId}/contract-items/${itemId}/datasource`, request);
  }

  public static getAutomations(contractItemId: string): Promise<ContractItemAutomations> {
    return axios.get(`/api/v1/contract-items/${contractItemId}/automations`)
      .then(({ data }: any) => new ContractItemAutomations(data));
  }

  public static getProductDescriptions(pagination: Pagination,
    businessUnitId: string,
    description: string) {
    return axios.get('/api/v1/contract-items/product-descriptions', {
      params: {
        page: pagination.page - 1,
        size: pagination.itemsPerPage,
        businessUnitId,
        description,
      },
    }).then((response: any) => Response.of(response.data));
  }

  public static putAutomations(
    contractItemId: string,
    automations: ContractItemAutomations,
  ): Promise<void> {
    return axios.put(`/api/v1/contract-items/${contractItemId}/automations`, automations);
  }

  public static existsInvoiceItem(contractItemId: string,
    invoiceCompetence: string) : Promise<boolean> {
    return axios
      .get(`/api/v1/contract-items/${contractItemId}?competence=${invoiceCompetence}`)
      .then(({ data }: any) => (data));
  }

  public static existsPreInvoiceItem(contractItemId: string,
    invoiceCompetence: string) : Promise<boolean> {
    return axios
      .get(`/api/v1/contract-items/${contractItemId}/exists-preinvoice?competence=${invoiceCompetence}`)
      .then(({ data }: any) => (data));
  }

  public static expireContract(contractId: string, data: RequestExpirationContract): Promise<void> {
    return axios.patch(`/api/v1/contracts/${contractId}/expire`, data);
  }

  public static cancelExpirationRequest(
    contractId: string,
    justificationId: string,
    observation: string,
  ): Promise<void> {
    return axios.patch(`/api/v1/contracts/${contractId}/expire/cancel`, { justificationId, observation });
  }

  public static getLinks(pagination: Pagination, contractId: string) {
    return axios.get(`/api/v1/contracts/${contractId}/links`, {
      params: {
        page: pagination.page - 1,
        size: pagination.itemsPerPage,
        sortBy: pagination.arrangeSortBy,
      },
    }).then((response: any) => Response.of(response.data));
  }

  public static deleteLink(linkId: string): Promise<any> {
    return axios.delete(`/api/v1/contracts/links/${linkId}`);
  }

  public static createLink(
    contractId: string,
    link: ContractLink,
  ): Promise<void> {
    return axios.post(`/api/v1/contracts/${contractId}/links`, link);
  }

  public static updateLink(
    link: ContractLink,
  ): Promise<void> {
    return axios.put(`/api/v1/contracts/links/${link.id}`, link);
  }

  public static switchPreview(
    businessUnitId: string,
    contractId: string,
    isPreview: boolean,
  ): Promise<any> {
    const body = { preview: isPreview };
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/switchpreview`, body);
  }

  public static switchCustomBilling(
    businessUnitId: string,
    contractId: string,
    isCustomBilling: boolean,
  ): Promise<any> {
    const body = { customBilling: isCustomBilling };
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/switchcustombilling`, body);
  }

  public static switchBillingDaily(
    businessUnitId: string,
    contractId: string,
    isBillingDaily: boolean,
  ): Promise<any> {
    const body = { billingDaily: isBillingDaily };
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/switchbillingdaily`, body);
  }

  public static switchBillingWithPushOrder(
    businessUnitId: string,
    contractId: string,
    billingPushOrder: boolean,
  ): Promise<any> {
    const body = { pushOrderRequired: billingPushOrder };
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/switchpushorder`, body);
  }

  public static switchReviewed(
    businessUnitId: string,
    contractId: string,
    contractReviewed: boolean,
    justification?: string,
  ): Promise<any> {
    const body = { reviewedRequired: contractReviewed, justification };
    return axios.put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/switchreviewed`, body);
  }

  public static persistPushOrder(
    businessUnitId: string,
    contractId: string,
    pushOrder: PushOrderPersist,
  ): Promise<void> {
    return axios.post(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/push-order`, pushOrder);
  }

  public static reOpenContract(
    businessUnitId: string,
    contractId: string,
    justificationText: string,
  ): Promise<any> {
    const body = { description: justificationText };
    return axios.post(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/reopen`, body);
  }

  public static reviewedContractItem(
    businessUnitId: string,
    contractItemId: string,
  ): Promise<any> {
    return axios.post(`/api/v1/contract-items/${contractItemId}/switchreview`);
  }

  public static updateContractAdditionalInfo(
    businessUnitId: string,
    contractId: string,
    additionalInfoContract: ContractAdditionalInfoRequest,
  ) : Promise<any> {
    return axios
      .put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/updateAdditionalInfo`,
        additionalInfoContract);
  }

  public static updateContractCancellationData(
    businessUnitId: string,
    contractId: string,
    cancellationDataRequest: ContractCancellationDataRequest,
  ) : Promise<any> {
    return axios
      .put(`/api/v1/business-units/${businessUnitId}/contracts/${contractId}/updateCancellationData`,
        cancellationDataRequest);
  }
}
